import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const DeleteAccount = () => {
  const { user, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (authLoading) return null;
  if (!user) return <Navigate to="/auth" replace />;

  const handleDelete = async () => {
    if (confirmText.trim().toUpperCase() !== "DELETE") {
      toast.error('Please type "DELETE" to confirm.');
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-account");
      if (error || (data as any)?.error) {
        throw new Error(error?.message || (data as any)?.error || "Failed to delete account");
      }
      toast.success("Your account has been deleted.");
      await signOut();
      navigate("/", { replace: true });
    } catch (e: any) {
      toast.error(e.message || "Could not delete your account. Please contact support.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="pt-32 pb-24">
        <div className="container mx-auto px-6 max-w-2xl">
          <div className="text-center mb-12">
            <p className="text-primary text-xs font-light tracking-[0.5em] uppercase mb-3">Account</p>
            <h1 className="font-serif text-4xl md:text-5xl font-bold italic">Delete Account</h1>
          </div>

          <div className="border border-destructive/30 rounded-lg p-8 bg-destructive/5 space-y-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="text-destructive shrink-0 mt-1" size={24} />
              <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
                <p className="font-medium text-foreground">This action is permanent and cannot be undone.</p>
                <p>Deleting your account will remove:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Your profile and contact information</li>
                  <li>Your booking history</li>
                  <li>Any rewards, coupons, or referral credits</li>
                  <li>Photos you have uploaded</li>
                  <li>Your sign-in credentials</li>
                </ul>
                <p className="text-xs text-muted-foreground/80">
                  Note: business records that we are legally required to retain (e.g., tax / payment receipts) may be kept in anonymized form.
                </p>
              </div>
            </div>

            <div className="space-y-3 pt-2">
              <Label htmlFor="confirm" className="text-xs tracking-[0.2em] uppercase">
                Type "DELETE" to confirm
              </Label>
              <Input
                id="confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                className="rounded-none"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => navigate("/account")}
                className="flex-1 rounded-none tracking-[0.2em] uppercase text-xs"
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={submitting || confirmText.trim().toUpperCase() !== "DELETE"}
                className="flex-1 rounded-none tracking-[0.2em] uppercase text-xs"
              >
                {submitting ? <Loader2 className="animate-spin" size={16} /> : "Delete My Account"}
              </Button>
            </div>
          </div>

          <p className="text-center text-xs text-muted-foreground/70 mt-8">
            Need help instead?{" "}
            <Link to="/account" className="text-primary hover:underline">
              Return to your account
            </Link>
          </p>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default DeleteAccount;
